import { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { differenceBy, isEmpty, map, chain, filter } from "lodash"
import Toast from "./components/toast"
import "./style.scss"

function findNewNotifications(notifications, stateNotifications) {
  return differenceBy(stateNotifications, notifications, "id")
}

function tagRemovedNotifications(notifications, stateNotifications) {
  const removedIds = chain(notifications)
    .differenceBy(stateNotifications, "id")
    .keyBy("id")
    .value()

  return map(notifications, notification => ({
    ...notification,
    removed: !!removedIds[notification.id],
  }))
}

function processIncomingNotifications(notifications, stateNotifications) {
  return [
    // Keep existing notifications, but tag removed ones
    ...tagRemovedNotifications(notifications, stateNotifications),
    // and add new notifications at the bottom of the pile
    ...findNewNotifications(notifications, stateNotifications),
  ]
}

export default function Toasters({ notifications: stateNotifications, dismiss }) {
  const [notifications, setNotifications] = useState([])
  const containerRef = useRef(null)
  const knownIdsRef = useRef(new Set())

  // We keep an internal state of notifications for animation purposes
  useEffect(() => {
    setNotifications(notifications => processIncomingNotifications(notifications, stateNotifications))

    // We clean removed notifications next time we receive them, on when we unmount
    return () => {
      setNotifications(notifications => filter(notifications, ({ removed }) => !removed))
    }
  }, [stateNotifications])

  useEffect(() => {
    const hasNewNotification = notifications.some(({ id, removed }) => !removed && !knownIdsRef.current.has(id))
    knownIdsRef.current = new Set(map(notifications, "id"))
    const container = containerRef.current
    if (hasNewNotification && container) {
      container.scrollTop = container.scrollHeight
    }
  }, [notifications])

  if (isEmpty(notifications)) {
    return null
  }

  return <div className="modules_notifications" ref={containerRef}>
    {map(notifications, notification =>
      <Toast notification={notification} key={notification.id} dismiss={dismiss} />,
    )}
  </div>
}

Toasters.propTypes = {
  dismiss: PropTypes.func.isRequired,
  notifications: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    level: PropTypes.oneOf(["error", "warning", "info", "success"]),
    title: PropTypes.string,
    message: PropTypes.string.isRequired,
  })).isRequired,
}
